import styled from 'styled-components';

import { color, cornerRadius, font, space } from '@creditornot/alchemy-react/tokens';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useUserLocale } from 'common/hooks/useUserLocale';

type Background = 'solid' | 'transparent';

export type FileColumn = {
  name: string;
  required?: boolean;
  info?: string;
  allowed?: string[];
  groupInfo?: string;
  footnote?: string;
  topInfo?: string;
  separator?: boolean;
};

type Props = {
  background?: Background;
  columns: FileColumn[];
};

const Root = styled.div`
  ${font.footnote};

  &[data-background='solid'] {
    background-color: ${color.bgSurface};
    border-radius: ${cornerRadius.medium};
    padding: ${space.regularXlarge};
  }
`;

const Column = styled.code<{ $separator?: boolean }>`
  border-bottom: ${(props) => (props.$separator ? `1px solid ${color.border}` : 'none')};
  display: flex;
  gap: ${space.regularXlarge};
  justify-content: space-between;
  margin-bottom: ${(props) => (props.$separator ? `8px` : '0')};
  padding-bottom: ${(props) => (props.$separator ? `8px` : '0')};
`;

const Info = styled.span`
  color: ${color.textSubdued};
`;

const Header = styled.div`
  border-top: 1px solid ${color.border};
  color: ${color.textSubdued};
  margin-top: ${space.regularMedium};
  padding-bottom: ${space.regularMedium};
  padding-top: ${space.regularMedium};
  white-space: break-spaces;
`;

const TopLabel = styled.div`
  color: ${color.textSubdued};
`;

const Required = styled.div`
  align-items: flex-end;
  color: ${color.textSubdued};
  display: flex;
`;

const Allowed = styled.div`
  color: ${color.textSubdued};
  padding-left: ${space.regularMedium};
`;

const Footnotes = styled.div`
  font-size: 0.75rem;
  color: ${color.textSubdued};
  padding-top: 16px;
`;

const Footnote = 'div';

const FootnoteNumber = styled.sup`
  color: ${color.textSubdued};
`;

export function FileFormatInfo({ background = 'solid', columns }: Props) {
  const format = useFormatMessage();
  const locale = useUserLocale();
  const requiredLabel = format('common.validation.required').toLocaleLowerCase(locale);
  const footnotes = columns.filter((column) => column.footnote).map((column) => column.footnote);

  let footnoteCounter = 0;

  return (
    <Root data-background={background}>
      {columns.map(({ name, required, info, allowed, footnote, groupInfo, topInfo, separator }) => {
        footnoteCounter = footnote ? footnoteCounter + 1 : footnoteCounter;

        return (
          <Column key={name} $separator={separator}>
            <div>
              {groupInfo && <Header>{groupInfo}</Header>}
              {topInfo && <TopLabel>{topInfo}</TopLabel>}
              {name}
              {footnote && <FootnoteNumber>{footnoteCounter}</FootnoteNumber>}{' '}
              {info && <Info>({info})</Info>}
              {allowed && (
                <Allowed>
                  ({format('components.file_format_info.allowed_value')}: {allowed.join(', ')})
                </Allowed>
              )}
            </div>
            {required && <Required>{requiredLabel}</Required>}
          </Column>
        );
      })}
      {!!footnotes.length && (
        <Footnotes>
          {footnotes.map((footnote, index) => (
            <Footnote key={`footnote-${index}`}>
              <sup>{index + 1}</sup> {footnote}
            </Footnote>
          ))}
        </Footnotes>
      )}
    </Root>
  );
}
